import { Button } from "components/buttons/button/Button";
import { type FormikProps, withFormik } from "formik";
import { isEmpty, some } from "lodash";
import { Component } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { Navigate, Route, Routes } from "react-router";
import { bindActionCreators } from "redux";
import { type CheckboxProps, Loader } from "semantic-ui-react";
import intersection from "lodash/intersection";

import EditGroupPacksIssueLicenseModalContainer from "components/assignmentModals/peopleIssueLicenseModal/EditGroupIssueLicenseModalContainer/EditGroupPacksIssueLicenseModalContainer";
import Segments from "components/navigation/segments/Segments";
import {
  AutoenrollmentGroupNotificationsWarning,
  ContentAssignment,
  ContentAssignmentModal,
  DeleteLinkButton,
  DuplicateLinkButton,
  EditPriorityLinkButton,
  GenericSectionHeader,
  RemoveLinkButton,
  RestrictedAddButton,
  RestrictedByTooltip,
  RestrictedResource,
  TimestampLabel,
} from "../../../../components";
import {
  DeleteGroupsConfirmationModal,
  RemoveMembersConfirmationModal,
  RemovePriorityConfirmationModal,
} from "../../../../components/people";
import { Enrollment } from "../../../../enums/groups";
import { bindAction, type ContentPriorityLevel, type UpdateParams } from "../../../../interfaces";
import { pluralize } from "../../../../utils/stringUtils";
import userListUtils from "../../../../utils/userListUtils";
import validationSchemas from "../../../../utils/validationSchemas";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import dataService from "../../../Application/services/dataServices/typedDataService";
import usersDataService from "../../../Application/services/dataServices/usersDataService";
import backgroundTask from "../../../BackgroundTasks/backgroundTask";
import * as backgroundTasksActionsRedux from "../../../BackgroundTasks/state/backgroundTasksActions";
import { ContentType, PeopleType } from "../../../Library/PeopleAssignments/types";
import * as notificationsActionsRedux from "../../../Notifications/state/notificationsActions";
import rolesDataService from "../../services/rolesDataService";
import * as addToContentActionsRedux from "../../state/slices/addToContentSlice";
import {
  type ConfirmationState,
  type ContentAssignmentModelItem,
  type GroupInfoForm,
  type IFlowAssignmentModelItem,
  type SelectedContentMap,
} from "../../types";
import * as usersFilterActionsRedux from "../../Users/UsersOverview/state/filterActionCreators";
import * as addGroupActionsRedux from "../AddGroup/state/addGroupActions";
import * as groupsOverviewActionsRedux from "../GroupsOverview/state/groupsOverviewActions";
import OwnersAssignmentModal from "./AddOwners/OwnersAssigmentModal/OwnersAssignmentModal";
import * as editGroupActionsRedux from "./editGroupActions";
import EditInfo from "./EditInfo/EditInfo";
import EditOwners from "./EditOwners/EditOwners";
import { removeUsersFromGroupTask } from "./EditPeople/backgroundTasks/removeUsersFromGroupTask";
import EditPeople from "./EditPeople/EditPeople";
import GroupPacksContainer from "./GroupPacks/GroupPacksContainer/GroupPacksContainer";

import RemovePriorityButton from "components/buttons/linkButtons/RemovePriorityButton/RemovePriorityButton";
import RemoveGroupLicenseConfirmationDialog from "components/packs/removeGroupLicenseConfirmationDialog/RemoveGroupLicenseConfirmationDialog";
import { FeatureFlags } from "featureFlags";
import { reset } from "features/Licensing/ContentAssignmentModalLicensingSteps/state/slices/licensingModalSlice";
import { LICENCE_CONTENT } from "features/People/constants";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { withRouter, type WithRouterProps } from "../../../../adapters/withRouter/withRouter";
import ChangePeopleContentPriorityModal from "../../../../components/assignmentModals/changePriorityModal/ChangePeopleContentPriorityModal";
import EditNotifySettings from "../../../../components/notifyStep/EditNotifySettings/EditNotifySettings";
import {
  AddPeopleToContentTypes,
  AssignmentPeopleContext,
  ContentTypesEnum,
  RolePermissions,
  RouteNames,
  Strings,
  TemplateTypes,
} from "../../../../enums";
import navigationUtils from "../../../../utils/navigationUtils";
import Observable from "../../../../utils/Observable";
import Restricted from "../../../Application/Restricted";
import { GroupFlowPriorityChangedSuccess } from "../../../Application/services/realTimeNotification/events/library/libraryEvents";
import * as peopleEventTypes from "../../../Application/services/realTimeNotification/events/people/peopleEvents";
import RtnEventsEmitter from "../../../Application/services/realTimeNotification/rtnEventsEmitter";
import { type LDProps } from "../../../LDProps";
import {
  editPeopleContentPriority,
  removeContentPrioritiesDistributed,
} from "../../../Library/PeopleAssignments/state/actions/commonActions";
import {
  getUpdatedPriorityItems,
  mapUserAssignmentsToPriorityItems,
} from "../../../Library/PeopleAssignments/utils/peopleAssignmentUtils";
import { videosStateSelector } from "../../../Library/Videos/state/selectors";
import { accountPacksSearchSelector } from "../../../Licensing/Packs/state/slices/accountPacksSearchSlice";
import {
  issuePackLicenseToPeople,
  removePackLicenseFromPeople,
} from "../../../Licensing/Packs/state/thunks/packPurchasedViewThunk";
import { defaultEnrollmentRule } from "../AddEditGroupForms/EnrollmentBuilder/utils";
import groupDomainUtils from "../AddEditGroupForms/groupDomainUtils";
import { resetEnrollmentUsers } from "../state/slices/enrollmentUsersSlice";
import { fetchGroupEnrollmentUsers } from "../state/thunks/enrollmentUsersThunk";
import "./editGroup.scss";
import { issueLicensePermissionPredicate } from "./issueLicensePermissionPredicate";
import GroupPerformance from "./Performance/GroupPerformance";
import { resetEditGroupInfo } from "./state/slices/editGroupInfoSlice";
import { fetchGroupInfo, saveGroupInfo } from "./state/thunks/editGroupInfoThunk";
import AddUserButton from "../../Users/UsersOverview/AddUserButton";
import { shouldShowLicenseModalForGroupUpload } from "../../../Licensing/ContentAssignmentModalLicensingSteps/state/thunks/licensingModalThunk";
import { fetchContent } from "../../state/thunks/contentAssignmentThunk";
import { setSelected } from "../../state/slices/contentAssignmentSlice";

export type EditGroupProps = EditGroupPropsOwn &
  FormikProps<GroupInfoForm> &
  WithRouterProps &
  PropsFromRedux &
  LDProps;

export interface EditGroupPropsOwn {
  addGroupActions: {
    fetchOwners: () => void;
  };
  addOwnersList: any;
  backgroundTasksActions: {
    addOperationV1: () => void;
    addOperationV2: () => void;
  };
  notificationsActions: {
    sendTransientNotification: () => void;
  };
  showAddToContentModal: boolean;
}

interface EditGroupState {
  showAddUsersModal: boolean;
  usersFileUploadModalOption: { showModal: boolean; showLicenceStep: boolean };
  showCsvRemoveUsersModal: boolean;
  showAddOwnersModal: boolean;
  showPeopleIssueLicenseModal: boolean;
  showDeleteConfirmation: boolean;
  isStepLoading: boolean;
  isLicenseConfirmed: boolean;
  showRemoveGroupMembersConfirmation: boolean;
  selectedEnrolledMembersIds: number[];
  clickedEnrolledMemberIdToRemove: number | null;
  confirmation: ConfirmationState;
  selectedPackIds: number[];
  clickedGroupPackIdToRemove: number;
  removeLicenseModal: {
    show: boolean;
    packsCount: number;
  };
}

const buttonText = "Manage Members";
const createUserText = "Add Manually";
const importUsersText = "Add via CSV";

const contentListViewRtnEvents = [GroupFlowPriorityChangedSuccess];

export class EditGroup extends Component<EditGroupProps, EditGroupState> {
  private readonly flexiblePriorityDueDate: boolean;
  private readonly isGroupsPerformanceEnabled: boolean;
  private readonly isGroupCsvUserImportEnabled: boolean;
  private readonly isContentSchedulingEnabled: boolean;
  private readonly selectedContent: SelectedContentMap;
  constructor(props: EditGroupProps) {
    super(props);
    this.selectedContent = new Map();

    this.state = {
      showAddUsersModal: false,
      usersFileUploadModalOption: { showModal: false, showLicenceStep: false },
      showCsvRemoveUsersModal: false,
      showAddOwnersModal: false,
      showPeopleIssueLicenseModal: false,
      showDeleteConfirmation: false,
      isStepLoading: false,
      isLicenseConfirmed: true,
      showRemoveGroupMembersConfirmation: false,
      selectedEnrolledMembersIds: [],
      clickedEnrolledMemberIdToRemove: null,
      confirmation: {
        blocks: [],
        priorityItems: [],
        show: false,
      },
      selectedPackIds: [],
      clickedGroupPackIdToRemove: 0,
      removeLicenseModal: {
        show: false,
        packsCount: 0,
      },
    };

    this.isGroupsPerformanceEnabled = !!this.props.flags?.[FeatureFlags.GroupPerformance];
    this.flexiblePriorityDueDate = !!this.props.flags?.[FeatureFlags.FlexiblePriorityDueDate];
    this.isGroupCsvUserImportEnabled = !!this.props.flags?.[FeatureFlags.GroupCsvUserImport];
    this.isContentSchedulingEnabled = !!this.props.flags?.[FeatureFlags.ContentScheduling];
  }

  segmentPaths = {
    ...(!!this.props.flags?.[FeatureFlags.GroupPerformance] && { configure: "configure" }),
    members: "members",
    users: "users",
    owners: "owners",
    content: "content",
    licenses: "licenses",
    notify: "notify",
    enrollment: "enrollment",
  };

  performanceIndex = 1;
  infoStepIndex = 2;
  membersStepIndex = 3;
  usersStepIndex = 4;
  ownersStepIndex = 5;
  contentStepIndex = 6;
  licensesStepIndex = 7;

  groupId = parseInt(this.props.params.id!);

  notificationId = groupDomainUtils.getAutoEnrolledGroupNotificationSettingsId(this.groupId);

  onChangePriorityObserver = new Observable();
  onClearPriorityObserver = new Observable();

  componentDidMount() {
    this.props.fetchGroupInfo(this.groupId);
    RtnEventsEmitter.subscribe(contentListViewRtnEvents, this.clearSelectedContent);
    RtnEventsEmitter.subscribe(peopleEventTypes.GroupDuplicated, this.onDuplicationGroup);
  }

  componentWillUnmount() {
    this.props.resetEditGroupInfo();
    RtnEventsEmitter.unsubscribe(contentListViewRtnEvents, this.clearSelectedContent);
    RtnEventsEmitter.unsubscribe(peopleEventTypes.GroupDuplicated, this.onDuplicationGroup);
  }

  hasGroupEditPermissions = () => this.props.userPermissions.includes(RolePermissions.GroupsManage);
  hasGroupDuplicatePermissions = () =>
    intersection(this.props.userPermissions, [RolePermissions.GroupsManage, RolePermissions.GroupsCreate]).length > 0;

  clearSelectedContent = () => {
    const context = this.isContentSchedulingEnabled ? ContentTypesEnum.Scheduled : ContentTypesEnum.Flows;
    this.props.setSelectedContentIds({ context, selected: [] });
    this.selectedContent.clear();
  };

  onCloseAddOwnersModal = () => {
    this.setState({ showAddOwnersModal: false });
  };

  getGroupRoleId = async () => {
    const tag = `group:${this.groupId}`;
    const role = await rolesDataService.getRoles({ tags: tag });
    return role.items.length === 1 ? role.items[0].id : null;
  };

  onAddOwnersConfirm = async (ownersToAddIds: number[]) => {
    const roleId = await this.getGroupRoleId();
    if (roleId) {
      const count = ownersToAddIds.length;
      const params: UpdateParams = {
        id: `AddOwnersToGroup`,
        title: "Add owners to group",
        getMessageIds: async () => {
          const response = await usersDataService.assignUsersToRoles(ownersToAddIds, [roleId]);
          return [response.data];
        },
        successTransientMessage: (
          <>
            <strong>Owners added!</strong>&nbsp;({count}) {pluralize("Group Owner", count)}{" "}
            {count === 1 ? "has" : "have"} been added to this group successfully.
          </>
        ),
        failureTransientMessage: "Adding owners to group failed!",
      };

      const {
        backgroundTasksActions: { addOperationV1 },
        notificationsActions: { sendTransientNotification },
      } = this.props;

      backgroundTask.updateEntity(params, {
        addOperation: addOperationV1,
        sendTransientNotification,
      });
    }
    this.onCloseAddOwnersModal();
  };

  renderOwnersAddModal = () => {
    const { isStepLoading, showAddOwnersModal } = this.state;
    const { fetchOwners } = this.props.addGroupActions;
    const {
      addOwnersList: { items, itemsCount, isLoading },
    } = this.props;

    return (
      showAddOwnersModal && (
        <OwnersAssignmentModal
          groupId={this.groupId}
          loadPage={fetchOwners}
          ownersAmount={itemsCount}
          isListLoading={isLoading}
          isStepLoading={isStepLoading}
          owners={items}
          onConfirm={this.onAddOwnersConfirm}
          showModal={showAddOwnersModal}
          onCancel={this.onCloseAddOwnersModal}
        />
      )
    );
  };

  renderAddToContentModal = () =>
    this.props.showAddToContentModal && <ContentAssignmentModal peopleType={PeopleType.Group} />;

  goToGroupsOverview = () => {
    this.props.navigate(`/${RouteNames.peopleGroups}`);
  };

  onSelectedEnrolledMembersChanged = (ids: number[]) => {
    this.setState({ selectedEnrolledMembersIds: ids });
  };

  onSelectedPacksChanged = (ids: number[]) => {
    this.setState({ selectedPackIds: ids });
  };

  goToEditUser = (userId: number) => {
    this.props.navigate(`/${RouteNames.peopleUsers}/${userId}`);
  };

  getCurrentStepIndex = () => {
    const {
      location: { pathname },
    } = this.props;
    let stepIndex;
    switch (pathname.split("/").at(-1)) {
      case this.segmentPaths.configure:
        stepIndex = this.infoStepIndex;
        break;
      case this.segmentPaths.members:
        stepIndex = this.membersStepIndex;
        break;
      case this.segmentPaths.enrollment:
        stepIndex = this.membersStepIndex;
        break;
      case this.segmentPaths.users:
        stepIndex = this.usersStepIndex;
        break;
      case this.segmentPaths.owners:
        stepIndex = this.ownersStepIndex;
        break;
      case this.segmentPaths.content:
        stepIndex = this.contentStepIndex;
        break;
      case this.segmentPaths.licenses:
        stepIndex = this.licensesStepIndex;
        break;
      default:
        stepIndex = this.isGroupsPerformanceEnabled ? this.performanceIndex : this.infoStepIndex;
    }

    return stepIndex;
  };

  handleImportUsers = () => {
    this.props.shouldShowLicenseStepForGroupUpload([this.groupId]).then((shouldShowStep) => {
      this.setState({ usersFileUploadModalOption: { showModal: true, showLicenceStep: shouldShowStep } });
    });
  };

  addMembersBtn = (hasPermission: boolean = true) => {
    const handleAddUser = () => this.setState({ showAddUsersModal: true });
    const handleRemoveUsers = () => this.setState({ showCsvRemoveUsersModal: true });

    return this.isGroupCsvUserImportEnabled ? (
      <AddUserButton
        buttonText={buttonText}
        createUserText={createUserText}
        importUsersText={importUsersText}
        shouldRender
        onRemoveUsers={this.props.membersCount ? handleRemoveUsers : undefined}
        createPermissions={[RolePermissions.GroupsManage]}
        onCreateUser={handleAddUser}
        onImportUsers={this.handleImportUsers}
      />
    ) : (
      <RestrictedAddButton
        buttonText={buttonText}
        onClick={handleAddUser}
        hasPermission={hasPermission}
        className="add-member"
      />
    );
  };

  onCloseUsersFileUploadModal = () => {
    this.props.editGroupActions.resetAddMembersSearch();
    this.props.resetLicensingModal();
    this.setState({ usersFileUploadModalOption: { showLicenceStep: false, showModal: false } });
  };

  onCloseUsersRemoveModal = () => {
    this.props.editGroupActions.resetAddMembersSearch();
    this.setState({ showCsvRemoveUsersModal: false });
  };

  removeMembersBtn = (
    <RemoveLinkButton
      className="remove-member-button"
      onClick={() => {
        this.setState({ showRemoveGroupMembersConfirmation: true });
      }}
    />
  );

  saveGroupInfoHandler = () => {
    const {
      saveGroupInfo,
      values: { settings, imageUrl, name, description },
    } = this.props;

    const newGroupInfo = {
      imageUrl,
      name,
      description,
    };

    const newSettings = { ...settings };

    saveGroupInfo(this.groupId, newGroupInfo, newSettings);
  };

  onCloseAddUsersModal = () => {
    this.props.editGroupActions.resetAddMembersSearch();
    this.props.resetLicensingModal();
    this.setState({ showAddUsersModal: false });
  };

  onDuplicateGroup = async (groupId: number) => {
    const params = {
      id: "DuplicateGroup",
      title: `Duplicate group`,
      getMessageIds: async () => {
        const {
          data: { messageIds },
        } = await dataService.groups.duplicateGroups([groupId]);
        return messageIds;
      },
      successTransientMessage: "Group has been duplicated!",
      failureTransientMessage: "Group duplicate failed!",
    };
    const {
      backgroundTasksActions: { addOperationV1 },
      notificationsActions: { sendTransientNotification },
    } = this.props;

    await backgroundTask.updateEntity(params, {
      addOperation: addOperationV1,
      sendTransientNotification,
    });
  };

  onDuplicationGroup = (payload: any) => {
    const duplicateId = payload.GroupId.toString();
    const currentTab = this.props.location.pathname.split("/").at(-1);
    this.props.fetchGroupInfo(duplicateId);
    this.props.navigate(`/${RouteNames.peopleGroups}/${duplicateId}/${currentTab}`);
  };

  onDeleteGroup = async (groupId: number) => {
    this.goToGroupsOverview();

    const params = {
      id: "DeleteGroup",
      title: "Group deletion",
      getMessageIdsV2: () => [`GroupDeleted_${groupId}`],
      callApi: () => dataService.groups.deleteGroups([groupId]),
      successTransientMessage: "Group deleted successfully!",
      failureTransientMessage: "Group delete failed!",
    };

    const {
      backgroundTasksActions: { addOperationV2 },
      notificationsActions: { sendTransientNotification },
    } = this.props;

    await backgroundTask.updateEntityV2(params, {
      addOperation: addOperationV2,
      sendTransientNotification,
    });
  };

  onRemoveEnrolledGroupMembers = () => {
    const {
      backgroundTasksActions: { addOperationV2 },
      notificationsActions: { sendTransientNotification },
    } = this.props;

    const { clickedEnrolledMemberIdToRemove, selectedEnrolledMembersIds } = this.state;
    const enrolmentMembersToRemoveIds = clickedEnrolledMemberIdToRemove
      ? [clickedEnrolledMemberIdToRemove]
      : selectedEnrolledMembersIds;

    removeUsersFromGroupTask(enrolmentMembersToRemoveIds, this.groupId, (params) =>
      backgroundTask.updateEntityV2(params, {
        addOperation: addOperationV2,
        sendTransientNotification,
      }),
    );

    this.setState({
      selectedEnrolledMembersIds: [],
      showRemoveGroupMembersConfirmation: false,
      clickedEnrolledMemberIdToRemove: null,
    });
  };

  getMembersStepButtons = () => {
    const isWithAutoEnroll = this.props.groupInfo?.isWithAutoEnroll;
    const { selectedEnrolledMembersIds } = this.state;
    let buttons: React.ReactElement[] = [];
    if (isWithAutoEnroll !== undefined && isWithAutoEnroll === Enrollment.Manual) {
      if (selectedEnrolledMembersIds.length > 0) {
        buttons = [this.removeMembersBtn];
      } else {
        buttons = [this.addMembersBtn(this.hasGroupEditPermissions())];
      }
    }
    return buttons;
  };

  getContentStepButtons = () => {
    if (this.props.groupInfo?.readOnly) {
      return;
    }

    if (!this.props.selectedFlowIds?.length) {
      return [this.addContentBtn()];
    }

    const assignments = [...this.selectedContent.values()];
    const unremovableFlowSelected = assignments.some((f) => !(f as IFlowAssignmentModelItem).canRemove);
    const hasEditPermission = this.hasGroupEditPermissions();

    return [
      <RestrictedByTooltip hasPermission={hasEditPermission} key="edit-priority-button">
        <EditPriorityLinkButton
          className="edit-priority-button"
          onClick={() => this.onEditPriorityClick()}
          isDisabled={!hasEditPermission}
        />
      </RestrictedByTooltip>,
      <RemovePriorityButton
        key="remove-priority-button"
        onClick={() => this.onClearPriorityClick(this.props.selectedFlowIds!)}
        isDisabled={unremovableFlowSelected || !this.hasGroupEditPermissions()}
        context="people"
        entityType={ContentType.Flow}
        peopleType={PeopleType.Group}
        showTooltip={unremovableFlowSelected}
        multiple
      />,
    ];
  };

  getLicensesStepButtons = () => {
    if (this.props.groupInfo?.readOnly) {
      return;
    }

    const { selectedPackIds } = this.state;
    if (selectedPackIds.length) {
      return [this.removePacksBtn];
    }
    return [this.issueLicenseHeaderBtn];
  };

  hideContentCreate = () => !LICENCE_CONTENT.includes(this.props.selectedType);

  addContentBtn = () => {
    return this.hideContentCreate() ? undefined : (
      <RestrictedAddButton
        className="add-content create-button"
        onClick={() => this.props.onAddContent(this.groupId)}
        hasPermission={this.hasGroupEditPermissions()}
        buttonText={Strings.modalTitles.addContent}
      />
    );
  };

  addOwnersBtn = (
    <RestrictedAddButton
      className="add-owners create-button"
      onClick={() => {
        this.onOpenAddOwnersModal();
      }}
      hasPermission={this.hasGroupEditPermissions()}
      buttonText="Add Owners"
    />
  );

  issueLicensesToGroup = (selectedPackIds: number[]) => {
    if (some(selectedPackIds)) {
      const groupAssignments = selectedPackIds.map((packId) =>
        userListUtils.mapToAssignmentItem(this.groupId, { id: packId }, "pack", AssignmentPeopleContext.Group),
      );

      this.props.issuePackLicense({
        peopleType: AssignmentPeopleContext.Group,
        items: groupAssignments,
      });
    }

    this.hideIssueLicenseModal();

    return Promise.resolve();
  };

  setShowPeopleIssueLicenseModal(show: boolean) {
    this.setState({
      showPeopleIssueLicenseModal: show,
    });
  }

  onOpenIssueLicenseModal = () => {
    this.setShowPeopleIssueLicenseModal(true);
  };

  hideIssueLicenseModal = () => {
    this.setShowPeopleIssueLicenseModal(false);
  };

  issueLicenseHeaderBtn = (
    <Restricted
      permissions={[]}
      permissionPredicate={issueLicensePermissionPredicate}
      renderContent={(hasAnyPermission) => (
        <RestrictedByTooltip hasPermission={hasAnyPermission}>
          <Button
            primary
            className="create-button"
            content="Issue License"
            onClick={this.onOpenIssueLicenseModal}
            disabled={!hasAnyPermission}
          />
        </RestrictedByTooltip>
      )}
    />
  );

  renderPeopleIssueLicenseModal = () => {
    const { accountId } = this.props;

    return (
      this.state.showPeopleIssueLicenseModal && (
        <EditGroupPacksIssueLicenseModalContainer
          showModal={this.state.showPeopleIssueLicenseModal}
          onCancel={this.hideIssueLicenseModal}
          accountId={accountId}
          onConfirm={this.issueLicensesToGroup}
          peopleId={this.groupId}
          assignmentPeopleContext={AssignmentPeopleContext.Group}
        />
      )
    );
  };

  onRemoveLicenseClick = async (packId: number = 0) => {
    const { selectedPackIds } = this.state;
    const packsCount = packId > 0 ? 1 : selectedPackIds.length;

    this.setState({
      removeLicenseModal: {
        show: true,
        packsCount,
      },
      clickedGroupPackIdToRemove: packId,
    });
  };

  packsToRemove = () => {
    const { selectedPackIds, clickedGroupPackIdToRemove } = this.state;
    return clickedGroupPackIdToRemove > 0 ? [clickedGroupPackIdToRemove] : selectedPackIds;
  };

  onRemoveGroupLicense = () => {
    const packsToRemove = this.packsToRemove();

    const licenses = packsToRemove.map((packId) =>
      userListUtils.mapToAssignmentItem(this.groupId, { id: packId }, "pack", AssignmentPeopleContext.Group),
    );

    this.props.removePackLicense({
      peopleType: AssignmentPeopleContext.Group,
      items: licenses,
    });

    this.closeRemoveLicenseConfirmation();
    this.setState({ selectedPackIds: [] });
  };

  closeRemoveLicenseConfirmation = () => {
    this.setState({
      removeLicenseModal: {
        show: false,
        packsCount: 0,
      },
      clickedGroupPackIdToRemove: 0,
      selectedPackIds: [],
    });
  };

  removePacksBtn = (
    <Restricted
      permissions={[]}
      permissionPredicate={issueLicensePermissionPredicate}
      renderContent={(hasAnyPermission) => (
        <RestrictedByTooltip hasPermission={hasAnyPermission}>
          <RemoveLinkButton
            className="remove-pack-button"
            onClick={this.onRemoveLicenseClick}
            isDisabled={!hasAnyPermission}
          />
        </RestrictedByTooltip>
      )}
    />
  );

  onOpenAddOwnersModal = () => {
    this.setState({ showAddOwnersModal: true });
  };

  isContentTabOpened = () => this.getCurrentStepIndex() === this.contentStepIndex;
  isInfoTabOpened = () => this.getCurrentStepIndex() === this.infoStepIndex;
  isPerformanceTabOpened = () => this.getCurrentStepIndex() === this.performanceIndex;

  renderHeaderButtons = (inherited: boolean) => {
    switch (this.getCurrentStepIndex()) {
      case this.membersStepIndex:
        return this.getMembersStepButtons();
      case this.contentStepIndex:
        return this.getContentStepButtons();
      case this.ownersStepIndex:
        return inherited ? null : [this.addOwnersBtn];
      case this.licensesStepIndex:
        return this.getLicensesStepButtons();
      default:
        return [];
    }
  };

  renderDropdownButtons = () => {
    const group = this.props.groupInfo;
    const { selectedEnrolledMembersIds, selectedPackIds } = this.state;
    if (
      group?.readOnly ||
      selectedEnrolledMembersIds.length ||
      (this.props.selectedFlowIds?.length && this.isContentTabOpened()) ||
      selectedPackIds.length
    ) {
      return null;
    }

    const buttons = [
      <DuplicateLinkButton
        key="duplicate-group"
        onClick={() => {
          this.onDuplicateGroup(group!.id);
        }}
        isDisabled={!this.hasGroupDuplicatePermissions()}
      />,
    ];

    if (group?.canEdit) {
      buttons.push(
        <DeleteLinkButton key="delete-group" onClick={() => this.setState({ showDeleteConfirmation: true })} />,
      );
    }

    return buttons;
  };

  renderConfirmationModals = () => {
    const { groupInfo } = this.props;
    const { selectedEnrolledMembersIds, showDeleteConfirmation, clickedEnrolledMemberIdToRemove, removeLicenseModal } =
      this.state;

    return (
      <>
        <DeleteGroupsConfirmationModal
          open={showDeleteConfirmation}
          onCancel={() => {
            this.setState({ showDeleteConfirmation: false });
          }}
          onContinue={() => {
            this.onDeleteGroup(groupInfo!.id);
          }}
        />
        <RemoveMembersConfirmationModal
          open={this.state.showRemoveGroupMembersConfirmation}
          usersCount={clickedEnrolledMemberIdToRemove ? 1 : selectedEnrolledMembersIds.length}
          onCancel={() => {
            this.setState({
              showRemoveGroupMembersConfirmation: false,
              clickedEnrolledMemberIdToRemove: null,
              selectedEnrolledMembersIds: [],
            });
          }}
          onContinue={this.onRemoveEnrolledGroupMembers}
          featureName="group"
        />
        <RemovePriorityConfirmationModal peopleType={PeopleType.Group} removeObserver={this.onClearPriorityObserver} />
        <RemoveGroupLicenseConfirmationDialog
          groupsCount={1}
          packsCount={removeLicenseModal.packsCount}
          triggerOpen={removeLicenseModal.show}
          onCancel={this.closeRemoveLicenseConfirmation}
          onContinue={this.onRemoveGroupLicense}
        />
      </>
    );
  };

  onIsAwareChanged = (_: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
    const isAware = data.checked;
    const {
      values: { settings },
    } = this.props;
    this.props.setFieldValue("settings", {
      ...settings,
      isAware,
      isVisibleToAll: isAware || null,
      isOpen: isAware || null,
    });
  };

  onVisibleToAllChanged = (_: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
    const isVisibleToAll = data.checked;
    const {
      values: { settings },
    } = this.props;
    this.props.setFieldValue("settings", {
      ...settings,
      isVisibleToAll,
    });
  };

  onIsOpenChanged = (_: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
    const isOpen = !data.checked;
    const {
      values: { settings },
    } = this.props;
    this.props.setFieldValue("settings", { ...settings, isOpen });
  };

  getFilterOptions = () => {
    const { usersFilterActions } = this.props;
    usersFilterActions.getFilterOptions();
  };

  renderPerformanceTab = () => {
    return <GroupPerformance groupId={this.groupId} />;
  };

  renderConfigureTab = () => {
    const { groupInfo, isGroupInfoLoading } = this.props;

    return (
      <EditInfo
        {...this.props}
        // Casting to avoid "missing fields" errors that
        // were not causing a problem before typescript
        groupInfo={groupInfo as unknown as GroupInfoForm}
        isInfoLoading={isGroupInfoLoading}
        onIsAwareChanged={this.onIsAwareChanged}
        saveGroupInfo={this.saveGroupInfoHandler}
        onVisibleToAllChanged={this.onVisibleToAllChanged}
        onIsOpenChanged={this.onIsOpenChanged}
        readonly={!this.hasGroupEditPermissions()}
      />
    );
  };

  renderChangePriorityModal = () => (
    <ChangePeopleContentPriorityModal
      onSave={(assignments: { [key in PeopleType]?: ContentPriorityLevel[] }) => {
        getUpdatedPriorityItems(assignments, (items: ContentPriorityLevel[]) => {
          this.props.editPriority(items, this.groupId, this.flexiblePriorityDueDate);
        });
        this.clearSelectedContent();
      }}
      onTriggerModalObserver={this.onChangePriorityObserver}
    />
  );

  onEditPriorityClick = (item?: IFlowAssignmentModelItem) => {
    const assignments = item ? [item] : [...this.selectedContent.values()];
    const priorityItems = mapUserAssignmentsToPriorityItems(
      assignments as ContentAssignmentModelItem[],
      ContentType.Flow,
      this.flexiblePriorityDueDate,
    );

    this.onChangePriorityObserver.notify(() => {}, { [PeopleType.Group]: priorityItems });
  };

  /* istanbul ignore next */
  onClearPriorityClick = (ids: number[]) => {
    this.onClearPriorityObserver.notify(() => {
      const flowItems = { [ContentType.Flow]: ids };
      this.props.removeContentPriorities(this.groupId, AssignmentPeopleContext.Group, flowItems);

      this.clearSelectedContent();
    });
  };

  renderContentTab = () => {
    const { groupInfo } = this.props;

    return (
      <div className="edit-group-content-step">
        <ContentAssignment
          entityId={this.groupId}
          contentMap={this.selectedContent}
          contextMenuButtonHandlers={{
            onEditPriority: this.onEditPriorityClick,
            onClearPriority: this.onClearPriorityClick,
          }}
          peopleType={this.props.isTemplateGroup ? PeopleType.TemplateGroup : PeopleType.Group}
          onAddContent={() => this.props.onAddContent(this.groupId)}
          disabled={!groupInfo?.canEdit || !this.hasGroupEditPermissions() || groupInfo?.readOnly}
          showScheduledContent
        />
      </div>
    );
  };

  renderLicensingTab = () => {
    const { groupInfo } = this.props;
    const { selectedPackIds } = this.state;
    return (
      <>
        <GroupPacksContainer
          issueLicenseBtn={this.issueLicenseHeaderBtn}
          groupId={this.groupId}
          accountId={this.props.accountId}
          sendTransientNotification={this.props.notificationsActions.sendTransientNotification}
          onRemoveLicenseClick={this.onRemoveLicenseClick}
          isReadOnly={groupInfo?.readOnly || !groupInfo?.canEdit}
          selectedPackIds={selectedPackIds}
          onSelectedPacksChanged={this.onSelectedPacksChanged}
        />
        {this.renderPeopleIssueLicenseModal()}
      </>
    );
  };

  renderPeopleTab = (isUsersTab?: boolean) => {
    const {
      groupInfo,
      groupSettings,
      editPeople,
      isGroupInfoLoading,
      membersSearch,
      addMembersSearch,
      fetchGroupUsersEnrollment,
      resetEnrollmentUsers,
      enrollmentUsers,
      enrollmentRules,
    } = this.props;

    const { showAddUsersModal, usersFileUploadModalOption, showCsvRemoveUsersModal, selectedEnrolledMembersIds } =
      this.state;
    return (
      <EditPeople
        groupId={this.groupId}
        editGroupActions={this.props.editGroupActions}
        editPeople={editPeople}
        addMembersBtn={this.addMembersBtn(this.hasGroupEditPermissions())}
        showAddPeopleModal={showAddUsersModal}
        usersFileUploadModalOption={usersFileUploadModalOption}
        showCsvRemoveUsersModal={showCsvRemoveUsersModal}
        handleImportUsers={this.handleImportUsers}
        onCloseUsersFileUploadModal={this.onCloseUsersFileUploadModal}
        onCloseUsersRemoveModal={this.onCloseUsersRemoveModal}
        onCloseAddUsersModal={this.onCloseAddUsersModal}
        enrollmentType={groupInfo?.isWithAutoEnroll}
        isGroupInfoLoading={isGroupInfoLoading}
        enrollmentRules={isEmpty(enrollmentRules) ? [defaultEnrollmentRule] : enrollmentRules}
        selectedUsers={selectedEnrolledMembersIds}
        onSelectedUsersChanged={this.onSelectedEnrolledMembersChanged}
        getFilterOptions={this.getFilterOptions}
        goToEditUser={this.goToEditUser}
        isReadOnly={groupInfo?.readOnly || !groupInfo?.canEdit || !this.hasGroupEditPermissions()}
        isFromTemplateGroup={this.props.isTemplateGroup}
        onRemoveMemberClick={(id: number) => {
          this.setState({
            showRemoveGroupMembersConfirmation: true,
            clickedEnrolledMemberIdToRemove: id,
          });
        }}
        search={membersSearch}
        addUsersSearch={addMembersSearch}
        disableNotifications={!groupSettings.isAware}
        deepLink
        fetchGroupEnrollmentUsers={fetchGroupUsersEnrollment}
        resetEnrollmentUsers={resetEnrollmentUsers}
        groupEnrollmentUsers={enrollmentUsers}
        hidePeople={groupInfo?.readOnly}
        showUsersList={isUsersTab || groupInfo?.isWithAutoEnroll !== Enrollment.Automatic}
      />
    );
  };

  renderOwnersTab = () => {
    const { editGroupActions, ownersList, editPeople, userPermissions, groupInfo, isGroupInfoLoading } = this.props;

    if (isGroupInfoLoading || isEmpty(groupInfo))
      return (
        <Loader data-testid="owners-loader" active>
          Loading
        </Loader>
      );

    return (
      <EditOwners
        groupId={this.groupId}
        inherited={groupInfo.readOnly}
        loadOwners={editGroupActions.fetchOwners}
        ownersOptions={ownersList}
        filterOptions={editPeople.filterOptions}
        getFilterOptions={this.getFilterOptions}
        disableSelect={() => groupInfo.readOnly || groupDomainUtils.hasReadOnlyOwnersAccess(userPermissions)}
      />
    );
  };

  renderNotifyTab = () => (
    <EditNotifySettings
      templateType={TemplateTypes.AddedToGroup}
      notificationId={this.notificationId}
      disabled={!this.props.groupSettings.isAware!}
      infoBannerContent={<AutoenrollmentGroupNotificationsWarning />}
      saveComplete={this.saveGroupInfoHandler}
    />
  );

  render() {
    const { groupInfo, isAccessAuthorized, params, location, navigate } = this.props;
    const readOnly = groupInfo?.readOnly;

    return (
      <RestrictedResource isAuthorized={isAccessAuthorized}>
        <section className="edit-group nested-content">
          <GenericSectionHeader
            title={groupInfo?.name || ""}
            titleForGA="Group Details"
            goBackAction={() => navigationUtils.goBackOrDefault(location, navigate, `/${RouteNames.peopleGroups}`)}
            dropdownButtons={!this.isPerformanceTabOpened() ? this.renderDropdownButtons() : null}
            buttons={this.renderHeaderButtons(readOnly!)}
            showReportLastUpdated={this.isPerformanceTabOpened()}
          />
          <div className="last-modified-label-container">
            {this.isInfoTabOpened() && (
              <TimestampLabel label="Last modified" dateTime={groupInfo?.dateModified} format="MM/DD/YYYY h:mm A" />
            )}
          </div>
          {this.renderConfirmationModals()}
          {this.renderAddToContentModal()}
          {this.renderOwnersAddModal()}
          {this.renderChangePriorityModal()}
          <Segments to={`/${RouteNames.peopleGroups}/${params.id}`}>
            {this.isGroupsPerformanceEnabled && <Segments.Segment label="Performance" />}
            {!readOnly && (
              <Segments.Segment
                label="Configure"
                {...(this.isGroupsPerformanceEnabled && { to: this.segmentPaths.configure })}
              />
            )}
            <Segments.Segment
              to={readOnly ? this.segmentPaths.enrollment : this.segmentPaths.members}
              label={readOnly ? "Enrollment" : "Members"}
            />
            {readOnly && <Segments.Segment to={this.segmentPaths.users} label="Users" />}
            <Segments.Segment to={this.segmentPaths.owners} label="Owners" />
            <Segments.Segment to={this.segmentPaths.content} label="Content" />
            <Segments.Segment to={this.segmentPaths.licenses} label="Licenses" />
            {!readOnly && this.props.groupInfo?.isWithAutoEnroll === Enrollment.Automatic && (
              <Segments.Segment to={this.segmentPaths.notify} label="Notify" />
            )}
          </Segments>
          <Routes>
            <Route
              path="/"
              element={this.isGroupsPerformanceEnabled ? this.renderPerformanceTab() : this.renderConfigureTab()}
            />
            {!readOnly && <Route path={this.segmentPaths.configure} element={this.renderConfigureTab()} />}
            <Route path={this.segmentPaths.members} element={this.renderPeopleTab()} />
            <Route path={this.segmentPaths.enrollment} element={this.renderPeopleTab()} />
            <Route path={this.segmentPaths.users} element={this.renderPeopleTab(true)} />
            <Route path={this.segmentPaths.owners} element={this.renderOwnersTab()} />
            <Route path={this.segmentPaths.content} element={this.renderContentTab()} />
            <Route path={this.segmentPaths.licenses} element={this.renderLicensingTab()} />
            <Route path={this.segmentPaths.notify} element={this.renderNotifyTab()} />
            <Route path="*" element={<Navigate to={readOnly ? this.segmentPaths.enrollment : "../"} replace />} />
          </Routes>
        </section>
      </RestrictedResource>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const editGroup = state.people.editGroup;
  const editGroupInfo = editGroup.editInfo;
  const assetsFilterOptions = videosStateSelector(state).overview.filters;

  return {
    accountId: state.userProfile.accountId,
    userPermissions: state.userProfile.permissions,
    backgroundTasks: state.backgroundTasks.tasks,
    groupSettings: editGroupInfo.settings,
    groupInfo: editGroupInfo.info,
    membersCount: editGroup.editPeople.usersList.itemsCount,
    isGroupInfoLoading: editGroupInfo.isLoading,
    isTemplateGroup: editGroupInfo.info?.readOnly,
    isAccessAuthorized: editGroupInfo.isAccessAuthorized,
    enrollmentRules: editGroupInfo.enrollmentRules,
    editPeople: {
      list: editGroup.editPeople.usersList,
      filterOptions: state.people.usersOverview.filterOptions,
      usersToAdd: editGroup.editPeople.usersToAdd,
    },
    ownersList: editGroup.ownersList,
    assetsFilterOptions,
    addOwnersList: state.people.addGroup.ownersList,
    showAddToContentModal: state.people.addToContentModal.showModal,
    membersSearch: editGroup.membersSearch,
    addMembersSearch: editGroup.addMembersSearch,
    accountPacksSearch: accountPacksSearchSelector(state),
    groupContentAssignment: state.people.contentAssignment,
    enrollmentUsers: state.people.enrollmentUsers,
    isShouldShowModalLoading: state.licensing.licensingModal.isLoading,
    selectedFlowIds: state.people.contentAssignment.content.selected.Flows,
    selectedType: state.people.contentAssignment.content.selectedType,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    fetchContentAction: bindActionCreators(fetchContent, dispatch),
    editGroupActions: bindActionCreators(editGroupActionsRedux, dispatch),
    backgroundTasksActions: bindActionCreators(backgroundTasksActionsRedux, dispatch),
    notificationsActions: bindActionCreators(notificationsActionsRedux, dispatch),
    usersFilterActions: bindActionCreators(usersFilterActionsRedux, dispatch),
    groupsOverviewActions: bindActionCreators(groupsOverviewActionsRedux, dispatch),
    addGroupActions: bindActionCreators(addGroupActionsRedux, dispatch),
    addToContentActions: bindActionCreators(addToContentActionsRedux, dispatch),
    onAddContent: (groupId: number) => {
      dispatch(addToContentActionsRedux.setSelectedContentType(AddPeopleToContentTypes.Flows));
      dispatch(addToContentActionsRedux.setSelectedPeopleIds([groupId]));
      dispatch(addToContentActionsRedux.setSelectedPeopleType(AssignmentPeopleContext.Group));
    },
    issuePackLicense: bindActionCreators(issuePackLicenseToPeople, dispatch),
    removePackLicense: bindActionCreators(removePackLicenseFromPeople, dispatch),
    editPriority: (priorityLevels: ContentPriorityLevel[], peopleId: number, useFlexibleDueDate: boolean) =>
      dispatch(editPeopleContentPriority(priorityLevels, peopleId, PeopleType.Group, useFlexibleDueDate)),
    removeContentPriorities: bindAction(removeContentPrioritiesDistributed, dispatch),
    resetLicensingModal: bindAction(reset, dispatch),
    fetchGroupUsersEnrollment: bindAction(fetchGroupEnrollmentUsers, dispatch),
    resetEnrollmentUsers: bindAction(resetEnrollmentUsers, dispatch),
    fetchGroupInfo: bindAction(fetchGroupInfo, dispatch),
    saveGroupInfo: bindAction(saveGroupInfo, dispatch),
    resetEditGroupInfo: () => dispatch(resetEditGroupInfo()),
    shouldShowLicenseStepForGroupUpload: bindAction(shouldShowLicenseModalForGroupUpload, dispatch),
    setSelectedContentIds: bindAction(setSelected, dispatch),
  };
};

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

/* istanbul ignore next */
const component = withFormik<EditGroupProps, any>({
  enableReinitialize: true,
  validationSchema: (props: EditGroupProps) => validationSchemas.getEditGroupSchema(parseInt(props.params.id!)),
  mapPropsToValues: (props: EditGroupProps) => {
    const info = props.groupInfo;
    const settings = props.groupSettings;
    return {
      name: info?.name,
      description: info?.description,
      imageUrl: info?.imageUrl,
      settings,
    };
  },
  handleSubmit: () => {
    // handler is required in order for submitForm`s returned promise to resolve
  },
})(EditGroup);

// @ts-ignore
const ConnectedComponent = connector(withRouter(withLDConsumer()(component)));
export default ConnectedComponent;
